import he from 'he';

// Function to validate if a string is a valid HTTP/HTTPS URL
/* Example Usage:
console.log(isValidHttpUrl('https://example.com')); // true
console.log(isValidHttpUrl('ftp://example.com'));   // false
console.log(isValidHttpUrl('example.com'));         // false
*/
export const isValidHttpUrl = (string) => {
  try {
    const url = new URL(string);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch (_) {
    return false;
  }
};

// This function is used to identify anchor text when analyzing internal links
/* Example Usage:
const html = '<a href="https://example.com">Click here</a> <a href="#">Home</a>';
console.log(extractAnchorTexts(html)); 
Output: ["Click here", "Home"]
*/
export const extractAnchorTexts = (html) => {
  const anchorRegex = /<a[^>]*>(.*?)<\/a>/gi;
  return [...html.matchAll(anchorRegex)].map((match) => he.decode(match[1]));
};

// Function to extract all href links from anchor tags in HTML
/* Example Usage:
const html = '<a href="https://example.com">Example</a> <a href="https://about">About</a>';
console.log(extractUrlsFromHtml(html)); 
Output: ["https://example.com", "/about"]
*/
export const extractUrlsFromHtml = (html) => {
  const anchorTags = [
    ...html.matchAll(/<a\s+[^>]*href=["']([^"']+)["'][^>]*>/gi),
  ];
  return anchorTags.map((match) => match[1]);
};

/* Example Usage:
console.log(normalizeUrl("https://www.example.com")); // "https://example.com"
console.log(normalizeUrl("http://www.example.com"));  // "http://example.com"
*/
export const normalizeUrl = (url) => {
  return url
    .trim()
    .toLowerCase()
    .replace(/^(https?:\/\/)www\./, '$1'); // Remove 'www.' but keep 'http://' or 'https://'
};

// Checks if a  URL exists exactly in the HTML content
/* Example Usage:
const html = '<a href="https://example.com">Example</a>';
console.log(isExactUrlInHtmlContent("https://example.com", html)); // true
console.log(isExactUrlInHtmlContent("http://example.com", html));  // false (different protocol)
*/
export const isExactUrlInHtmlContent = (word, actualHtmlContent) => {
  // Extract all URLs from <a> tags and standalone URLs
  const htmlUrls = extractUrlsFromHtml(actualHtmlContent);
  // Normalize the word and compare it with the extracted URLs
  const normalizedWord = normalizeUrl(word);

  return htmlUrls.some((url) => normalizeUrl(url) === normalizedWord);
};

// Checks if a word exists within an anchor tag in the HTML content
/* Example Usage:
const html = '<a href="https://example.com">Example</a>';
console.log(isWordInAnchorTag("Example", html)); // true
console.log(isWordInAnchorTag("Home", html));    // false
*/
export const isWordInAnchorTag = (word, htmlContent) => {
  const anchorTexts = extractAnchorTexts(htmlContent);
  return anchorTexts.some(
    (text) => text.trim().toLowerCase() === word.trim().toLowerCase()
  );
};

// Function to extract the base URL (protocol + domain + path)
/* Example Usage:
console.log(extractBaseUrl("https://example.com/page?query=123")); 
 Output: "https://example.com/page"
console.log(extractBaseUrl("ftp://example.com/page")); 
 Output: "Invalid URL" (and returns "")
*/
export const extractBaseUrl = (url) => {
  try {
    console.log('url', url);
    const urlObj = new URL(url);
    return `${urlObj.protocol}//${urlObj.host}${urlObj.pathname}`;
  } catch (error) {
    console.error('Invalid URL:', url);
    return ''; // Return an empty string if the URL is invalid
  }
};

// Function to check if the base URLs match
/* Example Usage:
const html = '<a href="https://example.com/page">Example</a>';
console.log(isBaseUrlMatching("https://example.com/page?query=123", html)); // true
console.log(isBaseUrlMatching("https://example.com/other", html)); // false
*/
export const isBaseUrlMatching = (boxUrl, htmlContent) => {
  const baseBoxUrl = extractBaseUrl(boxUrl);

  // Extract all URLs from the HTML content
  const extractedUrls = extractUrlsFromHtml(htmlContent);

  // Check if any of the extracted URLs has the same base URL
  return extractedUrls.some((textUrl) => {
    const baseTextUrl = extractBaseUrl(textUrl);
    return baseBoxUrl === baseTextUrl;
  });
};
